/**
 * Upload utility functions for file validation, formatting, and error handling
 * Optimized for performance and comprehensive error handling
 */
import { MAX_IMAGE_FILE_SIZE_LABEL } from './uploadConstants';

// Supported image formats for upload validation — single source of truth (#1040)
// Must match backend/src/image_processing/supported_formats.py
export const SUPPORTED_FORMATS = new Set([
  // Standard formats
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/gif',
  'image/tiff',
  'image/tif',
  // RAW camera formats
  'image/x-sony-arw',
  'image/x-canon-cr2',
  'image/x-canon-cr3',
  'image/x-canon-crw',
  'image/x-adobe-dng',
  'image/x-epson-erf',
  'image/x-nikon-nef',
  'image/x-olympus-orf',
  'image/x-pentax-pef',
  'image/x-fuji-raf',
  'image/x-panasonic-rw2',
  'image/x-panasonic-raw',
  'image/x-kodak-dcr',
  'image/x-minolta-mrw',
]);

// RAW file extensions for fallback detection when browser reports empty MIME type (#1040)
export const RAW_EXTENSIONS = new Set([
  '.arw', '.cr2', '.cr3', '.crw', '.dng', '.erf', '.nef',
  '.orf', '.pef', '.raf', '.rw2', '.raw', '.dcr', '.mrw',
]);

// File extensions to include in HTML accept attribute for RAW file picking
export const ACCEPT_EXTENSIONS = '.arw,.cr2,.cr3,.crw,.dng,.erf,.nef,.orf,.pef,.raf,.rw2,.raw,.dcr,.mrw,.heic,.heif';

// Extension-to-MIME mapping for resolving RAW file types when browser reports empty (#1040)
const EXTENSION_TO_MIME: Record<string, string> = {
  '.arw': 'image/x-sony-arw',
  '.cr2': 'image/x-canon-cr2',
  '.cr3': 'image/x-canon-cr3',
  '.crw': 'image/x-canon-crw',
  '.dng': 'image/x-adobe-dng',
  '.erf': 'image/x-epson-erf',
  '.nef': 'image/x-nikon-nef',
  '.orf': 'image/x-olympus-orf',
  '.pef': 'image/x-pentax-pef',
  '.raf': 'image/x-fuji-raf',
  '.rw2': 'image/x-panasonic-rw2',
  '.raw': 'image/x-panasonic-raw',
  '.dcr': 'image/x-kodak-dcr',
  '.mrw': 'image/x-minolta-mrw',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
};

/**
 * Check if a file is a RAW image by extension (fallback for empty MIME type)
 */
export const isRawFileByExtension = (filename: string): boolean => {
  const ext = filename.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  return RAW_EXTENSIONS.has(ext);
};

/**
 * Resolve MIME type from file extension when browser reports empty type.
 * Returns the file's original type if non-empty, otherwise maps the extension. (#1040)
 */
export const resolveFileMimeType = (file: File): string => {
  if (file.type) return file.type;
  const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  return EXTENSION_TO_MIME[ext] || '';
};

// Constants for better maintainability
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;
const BYTES_PER_GB = BYTES_PER_MB * 1024;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * 60;

/**
 * Validates if a file format is supported for upload.
 * Falls back to extension check for RAW files (browsers report empty MIME type). (#1040)
 */
export const validateFileFormat = (mimeType: string, filename?: string): boolean => {
  if (mimeType && typeof mimeType === 'string' && SUPPORTED_FORMATS.has(mimeType.toLowerCase())) {
    return true;
  }

  // Fallback: browsers often report empty MIME type for RAW camera files (#1040)
  if ((!mimeType || mimeType === '') && filename) {
    return isRawFileByExtension(filename);
  }

  return false;
};

/**
 * Formats file size in bytes to human-readable format
 * Optimized with constants and early returns
 */
export const formatFileSize = (bytes: number): string => {
  // Handle invalid inputs with early return
  const numBytes = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
  if (!numBytes || isNaN(numBytes) || numBytes < 0 || !isFinite(numBytes)) {
    return '0 B';
  }

  // Use optimized thresholds for better performance
  if (numBytes < BYTES_PER_KB) {
    return `${numBytes} B`;
  }

  if (numBytes < BYTES_PER_MB) {
    return `${(numBytes / BYTES_PER_KB).toFixed(1)} KB`;
  }

  if (numBytes < BYTES_PER_GB) {
    return `${(numBytes / BYTES_PER_MB).toFixed(1)} MB`;
  }

  return `${(numBytes / BYTES_PER_GB).toFixed(1)} GB`;
};

/**
 * Formats upload speed in bytes per second to human-readable format
 * Optimized with constants and early returns
 */
export const formatUploadSpeed = (bytesPerSecond: number): string => {
  // Handle invalid inputs with early return
  const numBytes = typeof bytesPerSecond === 'string' ? parseFloat(bytesPerSecond) : bytesPerSecond;
  if (!numBytes || isNaN(numBytes) || numBytes < 0 || !isFinite(numBytes)) {
    return '0 B/s';
  }

  // Use optimized thresholds for better performance
  if (numBytes < BYTES_PER_KB) {
    return `${numBytes} B/s`;
  }

  if (numBytes < BYTES_PER_MB) {
    return `${(numBytes / BYTES_PER_KB).toFixed(1)} KB/s`;
  }

  if (numBytes < BYTES_PER_GB) {
    return `${(numBytes / BYTES_PER_MB).toFixed(1)} MB/s`;
  }

  return `${(numBytes / BYTES_PER_GB).toFixed(1)} GB/s`;
};

/**
 * Calculates progress percentage from uploaded and total bytes
 */
export const calculateProgress = (uploaded: number, total: number): number => {
  if (!total || total <= 0 || !uploaded || uploaded < 0) {
    return 0;
  }

  const progress = (uploaded / total) * 100;

  // Clamp between 0 and 100, round to nearest integer
  return Math.min(100, Math.max(0, Math.round(progress)));
};

/**
 * Maps error codes to user-friendly error messages
 * Optimized with Map for better performance and maintainability
 */
const ERROR_MESSAGE_MAP = new Map([
  // Network errors
  ['network_error', 'Network error occurred. Please check your connection.'],
  ['err_network', 'Network error occurred. Please check your connection.'],

  // Server errors
  ['server_error', 'Server error occurred. Please try again later.'],
  ['internal_server_error', 'Server error occurred. Please try again later.'],

  // Authentication errors
  ['unauthorized', 'Authentication failed. Please log in again.'],
  ['auth_error', 'Authentication failed. Please log in again.'],

  // File validation errors
  ['invalid_format', 'Invalid file format. Please select a supported image format.'],
  ['file_too_large', `File is too large. Maximum file size is ${MAX_IMAGE_FILE_SIZE_LABEL}.`],
]);

export const mapErrorToMessage = (errorCode: string): string => {
  if (!errorCode || typeof errorCode !== 'string') {
    return 'Upload failed. Please try again.';
  }

  const code = errorCode.toLowerCase();

  // Check exact matches first (most common case)
  const exactMatch = ERROR_MESSAGE_MAP.get(code);
  if (exactMatch) {
    return exactMatch;
  }

  // Check partial matches for flexibility
  if (code.includes('network')) {
    return 'Network error occurred. Please check your connection.';
  }

  if (code.includes('server')) {
    return 'Server error occurred. Please try again later.';
  }

  if (code.includes('auth')) {
    return 'Authentication failed. Please log in again.';
  }

  // Default error message
  return 'Upload failed. Please try again.';
};

/**
 * Validates if a file is a valid image file
 */
export const isValidImageFile = (file: File): boolean => {
  if (!file || !file.type) {
    return false;
  }

  return validateFileFormat(file.type);
};

/**
 * Extracts file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return '';
  }

  return filename.substring(lastDotIndex + 1).toLowerCase();
};

/**
 * Formats time remaining in seconds to human-readable format
 * Optimized with constants for better readability and performance
 */
export const formatTimeRemaining = (seconds: number): string => {
  // Handle invalid inputs with early return
  if (!seconds || isNaN(seconds) || seconds < 0 || !isFinite(seconds)) {
    return '0s remaining';
  }

  const totalSeconds = Math.round(seconds);

  // Less than a minute
  if (totalSeconds < SECONDS_PER_MINUTE) {
    return `${totalSeconds}s remaining`;
  }

  // Less than an hour
  if (totalSeconds < SECONDS_PER_HOUR) {
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const remainingSeconds = totalSeconds % SECONDS_PER_MINUTE;
    return `${minutes}m ${remainingSeconds}s remaining`;
  }

  // Hours and minutes
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const remainingMinutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return `${hours}h ${remainingMinutes}m remaining`;
};

/**
 * Computes the aggregate privacy status for a list of uploaded images.
 *
 * Treats undefined visibility as 'private' because private is the system default
 * for all newly added images (they are never explicitly assigned a value on creation).
 * Without this treatment, a brand-new single-image upload would falsely report
 * 'mixed' instead of 'all-private'. Regression test for #1087.
 */
export const computeImagePrivacyStatus = (
  images: Array<{ imageId: string; visibility?: 'public' | 'private' }>
): 'all-public' | 'all-private' | 'mixed' | undefined => {
  if (images.length === 0) return undefined;
  // undefined visibility means the image was just added and defaults to private
  const effectiveVisibility = (img: { visibility?: 'public' | 'private' }) =>
    img.visibility ?? 'private';
  const allPublic = images.every(img => effectiveVisibility(img) === 'public');
  const allPrivate = images.every(img => effectiveVisibility(img) === 'private');
  if (allPublic) return 'all-public';
  if (allPrivate) return 'all-private';
  return 'mixed';
};

/**
 * Derives a display title from an image filename.
 * Strips the file extension, then replaces underscores and hyphens with spaces.
 * Collapses multiple consecutive separators and trims leading/trailing whitespace.
 * Used to auto-populate the title field on the very first image upload (#1225).
 */
export const deriveFilenameTitle = (filename: string): string => {
  if (!filename || typeof filename !== 'string') return '';
  // Strip file extension (last dot and everything after)
  const withoutExt = filename.replace(/\.[^.]+$/, '');
  // Replace underscores and hyphens with spaces, collapse runs, trim
  return withoutExt.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
};

/**
 * Pluralizes file count with proper singular/plural form
 */
export const pluralizeFileCount = (count: number): string => {
  // Handle invalid inputs
  if (!count || isNaN(count) || count < 0) {
    return '0 files';
  }

  const fileCount = Math.floor(count);

  if (fileCount === 1) {
    return '1 file';
  }

  return `${fileCount} files`;
};
